import { useCallback, useEffect, useState } from "react";
import firebase from "firebase/compat/app";

import { useAppDispatch } from "src/store";
import firebaseAuth from "src/services/firebaseAuth";
import { logOutUser, setUser, USER_INITIAL_DATA } from "src/store/actions";

export const useAuthObserver = () => {
  const dispatch = useAppDispatch();

  const [isObserverSet, setIsObserverSet] = useState<boolean>(false);
  const [isUserAuthenticated, setIsUserAuthenticated] =
    useState<boolean>(false);
  const [authError, setAuthError] = useState<Error | null>(null);

  // Clear any errors and force a refresh of the auth state
  const refreshAuth = useCallback(async () => {
    try {
      setAuthError(null);

      // Try to get the current auth instance
      const auth = firebaseAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        // Force token refresh
        await currentUser.getIdToken(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error refreshing auth:", error);
      setAuthError(error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, []);

  // Handle auth state changes
  const handleAuthChange = useCallback(
    async (user: firebase.User | null): Promise<void> => {
      try {
        // If we have a user, make sure they have a fresh token
        if (user) {
          try {
            // Refresh token and set user in state
            await user.getIdToken(true);

            dispatch(
              setUser({
                ...USER_INITIAL_DATA,
                id: user.uid,
                email: user.email ?? "",
              }),
            );

            setIsUserAuthenticated(true);
          } catch (error) {
            console.error("Auth token refresh error:", error);

            // Force logout if token refresh fails
            await firebaseAuth().signOut();
            dispatch(logOutUser());
            setIsUserAuthenticated(false);
          }
        } else {
          // No user, clear state
          dispatch(logOutUser());
          setIsUserAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        dispatch(logOutUser());
        setIsUserAuthenticated(false);
        setAuthError(error instanceof Error ? error : new Error(String(error)));
      } finally {
        setIsObserverSet(true);
      }
    },
    [dispatch],
  );

  // Set up the auth observer
  useEffect(() => {
    // Function to clean up the observer
    let unsubscribe = () => {};

    // Try to create the observer after a short delay
    const setupObserver = () => {
      try {
        unsubscribe = firebaseAuth().onAuthStateChanged(handleAuthChange);
      } catch (error) {
        console.error("Error setting up auth observer:", error);
        setAuthError(error instanceof Error ? error : new Error(String(error)));
        setIsObserverSet(true);
      }
    };

    // Give Firebase time to initialize fully
    const timerId = setTimeout(setupObserver, 200);

    // Clean up
    return () => {
      clearTimeout(timerId);
      unsubscribe();
    };
  }, [handleAuthChange]);

  // Try to refresh auth if we have an error
  useEffect(() => {
    if (authError) {
      const timerId = setTimeout(() => {
        refreshAuth().catch(console.error);
      }, 500);

      return () => clearTimeout(timerId);
    }
  }, [authError, refreshAuth]);

  return {
    isUserAuthenticated,
    isObserverSet,
    refreshAuth,
    authError,
  };
};
